import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import type { TeacherOverview } from "../../../lib/api/fieldApi";
import type { Sticker } from "../../../lib/api/socialApi";
import { useSocialApi } from "../../../lib/api/socialApi";
import { useFieldApi } from "../../../hooks/useFieldApi";
import { usePaletteSet } from "../../../hooks/usePaletteSet";
import { ApiError } from "../../../lib/http/errors";
import { Button } from "../../../components/ui/Button";
import { ErrorBanner, ScreenRoot } from "../../../components/Shell";
import { useTopBar } from "../../../components/TopBar";
import { useT } from "../../../i18n";
import { useTeacherApi } from "../api/teacherApi";
import type { CourseSettings, SaveUi, TeacherWorkItem } from "../types";
import { ReactionsTab, stickerChips } from "./ReactionsTab";
import { SettingsTab } from "./SettingsTab";
import { WorkEditor } from "./WorkEditor";
import { WorksTab } from "./WorksTab";
import { WorkThumb } from "./WorkThumb";
import "../../../styles/teacher.css";

/**
 * FS-11-PH1 '놀이터 설정' 탭 — 룸 없는 Phase 1 교사 콘솔 진입(SCR v0.6 별지 FS-11-PH1).
 *  - 상단 바 '⚙ 3학년 2반 · 놀이터 설정' + 탭 이름 · 'PH1 · 룸 없음' · 탭별 칩(5차시 마무리 놀이터 / 저장됨 / 작품 n · 스티커 n / 자동 추론 정확도 표시 없음).
 *  - 좌측 탭 한 틀: 놀이터 설정 · PH1(한눈에 보기 · 공개·프리셋·별명표(11-05) · 반응 집계(11-06) · 작품명·원리 태그(11-07))
 *    / 룸 콘솔 · PH2(룸 제어·학생 · 매칭·팀 편성 · 메모·성취 — 흐림, 누를 수 없음) / ↩ 교사 홈.
 *  - 한눈에 보기: 요약 4칸 · 작품 카드(썸네일 · 작품명 ✎ 바로 수정 · 원리 태그 · 반응 — 이름순 고정, 정렬 컨트롤 없음)
 *    · PH1 안내 배너 · 탭 열기 버튼 · '▶ 룸 열기 (PH2)' 흐림.
 * 데이터: GET …/teacher/overview(요약) · …/teacher/works(카드 — 탭끼리 공유, 편집하면 바로 반영) · 스티커 카탈로그 · 팔레트.
 */
const TABS = ["overview", "settings", "reactions", "works"] as const;
type Tab = (typeof TABS)[number];
const isTab = (v: string | null): v is Tab => !!v && (TABS as readonly string[]).includes(v);
const TOP_ICON: Record<Tab, string> = { overview: "⚙", settings: "⚙", reactions: "⭐", works: "🏷" };

export function PlaygroundSettingsScreen() {
  const t = useT();
  const { courseId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const tab: Tab = isTab(params.get("tab")) ? (params.get("tab") as Tab) : "overview";
  const api = useFieldApi();
  const teacher = useTeacherApi();
  const social = useSocialApi();
  const [data, setData] = useState<TeacherOverview | null>(null);
  const [works, setWorks] = useState<TeacherWorkItem[] | null>(null);
  const [worksFailed, setWorksFailed] = useState<string | null>(null);
  const [stickers, setStickers] = useState<Sticker[]>([]);
  // 작품마다 제 appearance.palette 이름의 팔레트(2026-09-30 팔레트 버전 전환)
  const palette = usePaletteSet(
    api,
    works?.map((w) => w.appearance),
  );
  const [saveUi, setSaveUi] = useState<SaveUi>("saved");
  const [editing, setEditing] = useState<string | null>(null);

  useEffect(() => {
    if (!api) return;
    let off = false;
    api
      .teacherOverview()
      .then((d) => !off && setData(d))
      .catch(() => {});
    return () => {
      off = true;
    };
  }, [api]);

  useEffect(() => {
    if (!teacher) return;
    let off = false;
    teacher
      .works()
      .then((w) => !off && setWorks(w))
      .catch((e: unknown) => {
        if (off) return;
        setWorks([]);
        setWorksFailed(e instanceof ApiError ? e.code : "NET-SERVER");
      });
    return () => {
      off = true;
    };
  }, [teacher]);

  useEffect(() => {
    if (!social) return;
    let off = false;
    social
      .stickers()
      .then((s) => !off && setStickers(s))
      .catch(() => {});
    return () => {
      off = true;
    };
  }, [social]);

  const onWorkSaved = useCallback(
    (w: TeacherWorkItem) =>
      setWorks((xs) => xs?.map((x) => (x.snapshotId === w.snapshotId ? w : x)) ?? xs),
    [],
  );
  const onSettingsSaved = useCallback(
    (s: CourseSettings) =>
      setData((d) =>
        d
          ? {
              ...d,
              visibility: s.visibility,
              minutes: Math.round(s.timeBudgetS / 60),
              preset: s.presetKey ? t(`course.preset.${s.presetKey}`) : d.preset,
            }
          : d,
      ),
    [t],
  );

  const today = data?.activities?.find((a) => a.status === "ready");
  const course = data?.courseLabel ?? courseId;
  const tabLabel: Record<Tab, string> = {
    overview: t("teacher.playground.tabOverview"),
    settings: t("teacher.playground.tabSettings"),
    reactions: t("teacher.playground.tabReactions"),
    works: t("teacher.playground.tabWorks"),
  };
  const stickerTotal =
    works?.reduce((n, w) => n + w.reactionTotal, 0) ?? data?.stickerTotal ?? null;
  const tabChip =
    tab === "overview"
      ? today
        ? { label: today.title, testId: "activity-chip" }
        : null
      : tab === "settings"
        ? {
            label:
              saveUi === "dirty"
                ? t("teacher.settings.state.dirty")
                : t(saveUi === "saving" ? "common.save.saving" : "common.save.saved"),
            testId: "save-chip",
          }
        : tab === "reactions"
          ? {
              label: t("teacher.reactions.topChip", {
                works: works?.length ?? 0,
                stickers: stickerTotal ?? 0,
              }),
              testId: "reaction-chip",
            }
          : { label: t("teacher.works.topChip"), testId: "tags-chip" };
  useTopBar({
    icon: TOP_ICON[tab],
    title:
      tab === "overview"
        ? t("teacher.playground.topTitle", { course })
        : t("teacher.playground.topTitleTab", { course, tab: tabLabel[tab] }),
    chips: [
      { label: t("teacher.playground.ph1Chip"), testId: "phase-chip" },
      ...(tabChip ? [tabChip] : []),
    ],
  });

  const go = (next: Tab) => setParams(next === "overview" ? {} : { tab: next });
  const dash = "—";
  const workTags = (works ?? []).flatMap((w) => w.tags);

  return (
    <ScreenRoot
      id="FS-11-PH1"
      title={t("teacher.playground.title")}
      hideTitle
      className="pf-console"
      focusKey={tab}
    >
      <div className="pf-console__frame">
        <nav className="pf-console__side" aria-label={t("teacher.playground.title")}>
          <p className="pf-console__group">{t("teacher.playground.groupPh1")}</p>
          <ul className="pf-console__tabs">
            {TABS.map((id) => (
              <li key={id}>
                <button
                  type="button"
                  className={`pf-console__tab${tab === id ? " pf-console__tab--on" : ""}`}
                  aria-current={tab === id ? "page" : undefined}
                  data-tab={id}
                  onClick={() => go(id)}
                >
                  {tabLabel[id]}
                </button>
              </li>
            ))}
          </ul>
          <p className="pf-console__group">{t("teacher.playground.groupPh2")}</p>
          <ul className="pf-console__tabs" data-testid="ph2-tabs">
            {(["tabRoom", "tabTeams", "tabNotes"] as const).map((k) => (
              <li key={k}>
                <span
                  className="pf-console__tab pf-console__tab--dim"
                  aria-disabled="true"
                  title={t("teacher.home.phase2Hint")}
                >
                  {t(`teacher.playground.${k}`)}
                </span>
              </li>
            ))}
          </ul>
          <Link to="/teacher" className="pf-console__tab pf-console__home">
            {t("teacher.playground.backHome")}
          </Link>
        </nav>

        <div className="pf-console__body" data-tab-panel={tab}>
          {worksFailed && tab !== "settings" && (
            <ErrorBanner
              cause={t("teacher.works.loadFailed")}
              action={t("teacher.common.retryLater")}
              code={worksFailed}
            />
          )}
          {tab === "overview" ? (
            <>
              <div className="pf-kpis" data-testid="pg-kpis">
                <div className="pf-kpi">
                  <span className="pf-muted">{t("teacher.playground.kpiVisibility")}</span>
                  <b>{data?.visibility ? t(`teacher.visibility.${data.visibility}`) : dash}</b>
                </div>
                <div className="pf-kpi">
                  <span className="pf-muted">{t("teacher.playground.kpiPreset")}</span>
                  <b>
                    {data?.preset && data.minutes != null
                      ? `${data.preset} · ${t("teacher.home.minutes", { n: data.minutes })}`
                      : dash}
                  </b>
                </div>
                <div className="pf-kpi">
                  <span className="pf-muted">{t("teacher.home.kpiUploaded")}</span>
                  <b>
                    {data?.submissions
                      ? `${data.submissions.uploaded} / ${data.submissions.total}`
                      : dash}
                  </b>
                </div>
                <div className="pf-kpi">
                  <span className="pf-muted">{t("teacher.playground.kpiStickers")}</span>
                  <b>{stickerTotal ?? dash}</b>
                </div>
              </div>
              <section className="pf-pane pf-pane--hero" aria-labelledby="pg-works">
                <div className="pf-row">
                  <h2 id="pg-works" className="pf-h3">
                    {t("teacher.playground.worksTitle")}
                  </h2>
                  <span className="pf-muted pf-row__end">{t("teacher.playground.byName")}</span>
                </div>
                {works?.length ? (
                  <ul className="pf-work-grid pf-work-grid--scroll" data-testid="pg-works">
                    {works.map((w) => {
                      const on = editing === w.snapshotId;
                      return (
                        <li
                          key={w.snapshotId}
                          className={`pf-work-tile${on ? " pf-work-tile--editing" : ""}`}
                          data-work={w.snapshotId}
                        >
                          <WorkThumb
                            work={w}
                            palette={palette}
                            label={t("teacher.reactions.previewLabel", { name: w.nickname })}
                          />
                          <b>{w.nickname}</b>
                          {on && teacher ? (
                            <WorkEditor
                              key={w.snapshotId}
                              work={w}
                              api={teacher}
                              variant="tile"
                              onSaved={(x) => {
                                onWorkSaved(x);
                                setEditing(null);
                              }}
                              onClose={() => setEditing(null)}
                            />
                          ) : (
                            <span className="pf-muted">
                              {w.title}{" "}
                              <button
                                type="button"
                                className="pf-edit"
                                aria-label={t("teacher.playground.editTitle", {
                                  name: w.nickname,
                                })}
                                data-testid="tile-edit"
                                onClick={() => setEditing(w.snapshotId)}
                              >
                                ✎
                              </button>
                            </span>
                          )}
                          {!on && (
                            <span className="pf-row pf-row--wrap pf-work-tile__chips">
                              {w.static && (
                                <span className="pf-chip-static" data-static="">
                                  {t("teacher.playground.staticChip")}
                                </span>
                              )}
                              {w.tags.map((k) => (
                                <span key={k} className="pf-chip-static pf-chip-static--info">
                                  {k}
                                </span>
                              ))}
                              {stickerChips(w.reactionCounts, stickers).map((s) => (
                                <span
                                  key={s.key}
                                  className="pf-chip-static pf-chip-static--ok"
                                  title={s.name}
                                >
                                  {s.emoji} {s.n}
                                </span>
                              ))}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="pf-muted" data-testid="pg-no-works">
                    {works === null ? t("common.loading") : t("teacher.playground.noWorks")}
                  </p>
                )}
                <div className="pf-banner pf-banner--info" role="note" data-testid="ph1-note">
                  <div className="pf-banner__text">{t("teacher.playground.ph1Note")}</div>
                </div>
              </section>
              <div className="pf-row pf-row--wrap">
                <Button variant="ghost" className="pf-btn--sm" onClick={() => go("settings")}>
                  {t("teacher.playground.openSettings")}
                </Button>
                <Button variant="ghost" className="pf-btn--sm" onClick={() => go("reactions")}>
                  {t("teacher.playground.openReactions")}
                </Button>
                <Button variant="ghost" className="pf-btn--sm" onClick={() => go("works")}>
                  {t("teacher.playground.openWorks")}
                </Button>
                <span
                  className="pf-btn pf-btn--ghost pf-btn--sm pf-btn--phase2 pf-row__end"
                  aria-disabled="true"
                  data-testid="open-room"
                >
                  {t("teacher.playground.openRoom")}
                </span>
              </div>
            </>
          ) : !teacher ? (
            <p className="pf-muted">{t("common.loading")}</p>
          ) : tab === "settings" ? (
            <SettingsTab
              api={teacher}
              workTags={workTags}
              onSaveState={setSaveUi}
              onSaved={onSettingsSaved}
            />
          ) : tab === "reactions" ? (
            <ReactionsTab works={works} stickers={stickers} palette={palette} />
          ) : (
            <WorksTab api={teacher} works={works} onSaved={onWorkSaved} />
          )}
        </div>
      </div>
    </ScreenRoot>
  );
}
